import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchGstReturnSummary, getGSTReports } from '../../../../../store/dashboard/revenueVendorExpenses/revenueVendorExpensesApiHelpers';
import {
    GstConnectionStatus,
    GstReturnSummaryResponseBody,
    GstSummaryView,
} from '../../../../../store/dashboard/revenueVendorExpenses/types';
import { buildGstRangeParams } from '../../../../../utils/dateUtils';
import { GST_RANGE_KEYS, GstRangeKey } from '../../GstOverview/useGstDateRange';
import { parseGstReturnSummary } from './gstSummaryAdapter';
import { GST_SUMMARY_MAX_POLLS, GST_SUMMARY_POLL_INTERVAL_MS, GST_SUMMARY_PROCESSING_STATUS } from './constants';

interface UseGstSummaryDataArgs {
    view: GstSummaryView;
    returnType: string;
    rangeKey: GstRangeKey;
    fetchStartDate: string;
    fetchEndDate: string;
    fetchHeadlinePanelData?: (isPolling?: boolean) => void;
}

/**
 * Fetches the GST return summary for the applied Time Period range, refetches when the range changes,
 * and re-polls (up to a cap) while the GST report is still PROCESSING.
 */
export function useGstSummaryData({
    view,
    returnType,
    rangeKey,
    fetchStartDate,
    fetchEndDate,
    fetchHeadlinePanelData,
}: UseGstSummaryDataArgs) {
    const { id: assesseeOrgId } = useParams();
    const [loading, setLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [responseBody, setResponseBody] = useState<GstReturnSummaryResponseBody | null>(null);
    const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pollingCount = useRef(0);
    const pollingExhaustedRef = useRef(false);
    const isMountedRef = useRef(true);
    // Bumped on every fetch trigger so a response from a superseded range is dropped.
    const fetchGenRef = useRef(0);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const fetchData = (orgId: string, isPollingCall = false, gen = fetchGenRef.current) => {
        if (!isPollingCall) {
            setLoading(true);
            setHasError(false);
        } else pollingCount.current += 1;
        const isCurrent = () => isMountedRef.current && gen === fetchGenRef.current;
        fetchGstReturnSummary(
            { orgId, view, returnType, ...buildGstRangeParams(rangeKey, fetchStartDate, fetchEndDate) },
            {
                onSuccess: (body: GstReturnSummaryResponseBody) => {
                    if (!isCurrent()) return;
                    setResponseBody(body);
                    // A successful poll replaces an earlier failure, so the error label must not stay beside the table.
                    setHasError(false);
                },
                onError: (err?: unknown) => {
                    if (err) console.error('Error in fetching GST return summary', err);
                    // A failed background poll keeps the same-range data on screen.
                    if (!isCurrent() || isPollingCall) return;
                    // Never keep showing the previous range's figures as if they were current.
                    setResponseBody(null);
                    setHasError(true);
                },
            },
        ).finally(() => {
            if (isCurrent()) setLoading(false);
        });
    };

    // The poll interval is created once per org, so it must always call the latest fetchData and
    // headline refresh (current range) rather than the closures captured when the interval started.
    const fetchDataRef = useRef(fetchData);
    const fetchHeadlinePanelDataRef = useRef(fetchHeadlinePanelData);
    useEffect(() => {
        fetchDataRef.current = fetchData;
        fetchHeadlinePanelDataRef.current = fetchHeadlinePanelData;
    });

    const stopPolling = () => {
        if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        pollingCount.current = 0;
        if (isMountedRef.current) setIsProcessing(false);
    };

    const checkReportStatus = (orgId: string, isCancelled: () => boolean) => {
        getGSTReports(
            { assesseeOrgId: orgId },
            {
                onSuccess: (reports: { status?: string }[]) => {
                    if (isCancelled()) return;
                    const isProcessingReport = reports?.some((report) => report.status === GST_SUMMARY_PROCESSING_STATUS);
                    if (!isProcessingReport) {
                        if (pollingIntervalRef.current) stopPolling();
                        return;
                    }
                    if (pollingIntervalRef.current || pollingExhaustedRef.current) return;
                    setIsProcessing(true);
                    pollingIntervalRef.current = setInterval(() => {
                        if (pollingCount.current >= GST_SUMMARY_MAX_POLLS) {
                            pollingExhaustedRef.current = true;
                            stopPolling();
                            return;
                        }
                        checkReportStatus(orgId, isCancelled);
                        fetchDataRef.current(orgId, true);
                        fetchHeadlinePanelDataRef.current?.(true);
                    }, GST_SUMMARY_POLL_INTERVAL_MS);
                },
                // A failed status check leaves polling as it is; the next tick (or the next org) checks again.
                onError: () => {},
            },
        );
    };

    useEffect(() => {
        if (!assesseeOrgId) return;
        let cancelled = false;
        pollingExhaustedRef.current = false;
        checkReportStatus(assesseeOrgId, () => cancelled);
        return () => {
            cancelled = true;
            stopPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assesseeOrgId]);

    useEffect(() => {
        if (!assesseeOrgId) return;
        // A CUSTOM range is only applied once both month chips are set; hold the previous range until then.
        if (rangeKey === GST_RANGE_KEYS.CUSTOM && (!fetchStartDate || !fetchEndDate)) return;
        fetchGenRef.current += 1;
        fetchData(assesseeOrgId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assesseeOrgId, view, returnType, rangeKey, fetchStartDate, fetchEndDate]);

    const model = useMemo(() => parseGstReturnSummary(responseBody?.gstReturnSummary, view), [responseBody, view]);
    const gstConnectionStatus: GstConnectionStatus = useMemo(() => responseBody?.gstConnectionStatus || {}, [responseBody]);

    return { loading, hasError, isProcessing, model, gstConnectionStatus };
}
